import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Zap, Bell, ShieldCheck, FileText, Loader2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { SupportForm } from "@/components/SupportForm";
import { useAuth } from "@/context/AuthContext";

export default function Account() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([api.get("/commerce/me"), api.get("/notifications/preferences")])
    .then(([m, p]) => { setMe(m.data); setPrefs(p.data); }).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const cancel = async () => {
    setBusy(true);
    try { const { data } = await api.post("/commerce/cancel"); if (data.ok) toast.success("Subscription will cancel at period end. Your competency history is preserved."); else toast.error(data.error || "No active subscription"); await load(); }
    catch (e) { toast.error(apiErr(e)); } setBusy(false);
  };
  const manageBilling = async () => {
    setBusy(true);
    try { const { data } = await api.post("/commerce/portal", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };
  const togglePref = async (key) => {
    const next = { [key]: !prefs[key] };
    setPrefs({ ...prefs, ...next });
    try { const { data } = await api.put("/notifications/preferences", next); setPrefs(data); } catch (e) { toast.error(apiErr(e)); }
  };

  if (!me || !prefs) return <div className="p-8 text-tc-muted" data-testid="account-loading">Loading account…</div>;
  const ai = me.ai_usage;
  const pct = ai.included ? Math.min(100, Math.round((ai.used / ai.included) * 100)) : 0;

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto space-y-5" data-testid="account-page">
      <h1 className="font-heading text-3xl font-extrabold">Account & Settings</h1>

      <div className="tc-card p-5" data-testid="account-plan">
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> Plan & Billing</div>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
          <div>
            <div className="text-xl font-bold">{me.plan.name}</div>
            {me.pro_week && <div className="text-tc-sub text-sm" data-testid="account-pro-week">One-time Pro week, earned at Level 8 — ends {new Date(me.pro_week.expires_at).toLocaleString()}. No payment, no renewal.</div>}
            {me.inherited_from_family && <div className="text-tc-sub text-sm" data-testid="account-family-inherited">Included through your linked Family plan.</div>}
            {me.comp && <div className="text-tc-sub text-sm" data-testid="account-comp">Complimentary plan granted by TRADECRAFT. No billing, no renewal.</div>}
            {!me.comp && <div className="text-tc-sub text-sm">{me.subscription?.status ? `Status: ${me.subscription.status}${me.subscription.cancel_at_period_end ? " (cancels at period end)" : ""}` : "No active subscription"}</div>}
          </div>
          <div className="flex gap-2">
            {me.subscription?.stripe_customer_id && <button data-testid="account-manage-billing" onClick={manageBilling} disabled={busy} className="rounded-lg bg-tc-coach text-tc-bg font-semibold px-4 py-2 text-sm">Manage billing</button>}
            <button data-testid="account-change-plan" onClick={() => navigate("/pricing")} className="rounded-lg bg-tc-coach text-tc-bg font-semibold px-4 py-2 text-sm">{me.tier === "FREE" ? "Upgrade" : "Change plan"}</button>
            {me.tier !== "FREE" && me.tier !== "PRO_WEEK" && me.subscription?.status && <button data-testid="account-cancel" onClick={cancel} disabled={busy} className="rounded-lg border border-white/15 px-4 py-2 text-sm text-tc-sub">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cancel"}</button>}
          </div>
        </div>
      </div>

      <div className="tc-card p-5" data-testid="account-ai-usage">
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> AI Usage</div>
        <div className="flex items-center justify-between mt-3 text-sm">
          <span className="text-tc-sub">{ai.unlimited ? "Unlimited (admin)" : `${ai.used} of ${ai.included} included analyses used ${me.tier === "PRO_WEEK" ? "this Pro week" : "this month"}`}</span>
          {ai.warn && !ai.limit_reached && <span className="text-tc-xp text-xs">Approaching limit</span>}
          {ai.limit_reached && <span className="text-tc-bear text-xs">Limit reached</span>}
        </div>
        {!ai.unlimited && <div className="h-2 rounded-full bg-white/5 mt-2 overflow-hidden"><div className={`h-full rounded-full ${ai.limit_reached ? "bg-tc-bear" : "bg-tc-coach"}`} style={{ width: `${pct}%` }} /></div>}
        <p className="text-tc-muted text-[11px] mt-2">Deterministic market analysis is always available, even at the AI limit.</p>
      </div>

      <div className="tc-card p-5" data-testid="account-support">
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach mb-3">Support</div>
        <p className="text-tc-sub text-sm mb-3">Questions about your account, billing or the product? Send us a message and we'll reply by email.</p>
        <SupportForm idPrefix="account-support" defaultName={user?.name || ""} defaultEmail={user?.email || ""} />
      </div>
      <div className="tc-card p-5" data-testid="account-notifications">
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach flex items-center gap-1.5"><Bell className="w-3.5 h-3.5" /> Notifications</div>
        <div className="mt-3 space-y-2">
          {[["market_intelligence", "Market intelligence"], ["watched_situations", "Watched situations"], ["daily_brief", "Daily brief"], ["learning_progress", "Learning & progress"], ["account_billing", "Account & billing"]].map(([k, label]) => (
            <label key={k} className="flex items-center justify-between text-sm cursor-pointer">
              <span className="text-tc-txt">{label}</span>
              <button data-testid={`pref-${k}`} onClick={() => togglePref(k)} className={`w-10 h-5 rounded-full transition-colors relative ${prefs[k] ? "bg-tc-coach" : "bg-white/10"}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${prefs[k] ? "left-5" : "left-0.5"}`} />
              </button>
            </label>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <button data-testid="account-security" onClick={() => api.post("/auth/send-verification").then(() => toast.success("Verification email requested — check your inbox.")).catch((e) => toast.error(apiErr(e)))}
          className="tc-card p-5 text-left hover:border-tc-coach/40"><div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Security</div><p className="text-tc-sub text-sm mt-2">Verify your email address.</p></button>
        <button data-testid="account-legal" onClick={() => navigate("/legal")} className="tc-card p-5 text-left hover:border-tc-coach/40"><div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Legal & Trust</div><p className="text-tc-sub text-sm mt-2">Terms, privacy, AI transparency & disclaimers.</p></button>
      </div>
    </div>
  );
}
